import type { EditorState } from '@atlaskit/editor-prosemirror/state';

export interface PositionValidationResult {
	errors: string[];
	sanitized: {
		endPos: number;
		startPos: number;
	};
	valid: boolean;
}

/**
 * Validates and sanitizes positions for streaming operations
 */
export function validatePositions(
	state: EditorState,
	startPos: number | undefined,
	endPos: number | undefined,
): PositionValidationResult {
	const errors: string[] = [];
	const docSize = state.doc.content.size;

	let safeStart = startPos ?? 0;
	let safeEnd = endPos ?? docSize;

	// Validate startPos
	if (startPos === undefined) {
		errors.push('startPos is undefined');
	} else {
		if (startPos < 0) {
			errors.push(`startPos ${startPos} is negative`);
			safeStart = 0;
		}
		if (startPos > docSize) {
			errors.push(`startPos ${startPos} exceeds docSize ${docSize}`);
			safeStart = docSize;
		}
	}

	// Validate endPos
	if (endPos === undefined) {
		errors.push('endPos is undefined');
	} else {
		if (endPos < 0) {
			errors.push(`endPos ${endPos} is negative`);
			safeEnd = 0;
		}
		if (endPos > docSize) {
			errors.push(`endPos ${endPos} exceeds docSize ${docSize}`);
			safeEnd = docSize;
		}
	}

	// Validate relationship
	if (safeEnd < safeStart) {
		errors.push(`endPos ${safeEnd} is less than startPos ${safeStart}`);
		safeEnd = safeStart;
	}

	return {
		valid: errors.length === 0,
		errors,
		sanitized: { startPos: safeStart, endPos: safeEnd },
	};
}

/**
 * Clamps a position to valid document bounds
 */
export function clampPosition(pos: number, docSize: number): number {
	return Math.max(0, Math.min(pos, docSize));
}

/**
 * Validates a range and returns safe bounds
 */
export function safeRange(
	from: number | undefined,
	to: number | undefined,
	docSize: number,
): { from: number; to: number } | null {
	if (from === undefined || to === undefined) {
		return null;
	}

	const safeFrom = clampPosition(from, docSize);
	const safeTo = clampPosition(to, docSize);

	if (safeFrom > safeTo) {
		return null;
	}

	return { from: safeFrom, to: safeTo };
}
